import type { CtwFetchFn } from '@ctw/shared/context/ctw-context';
import { type Env, getBffApiBaseUrl, getStandaloneBaseUrl } from '@ctw/shared/utils/environments';

export interface RiskGapStatusUpdateAttributes {
  status: string;
  substatuses?: Array<string>;
  note?: string;
}

export interface SubmitRiskGapStatusUpdateArgs {
  ctwFetch: CtwFetchFn;
  env: Env;
  authToken: string;
  riskGapId: string;
  attributes: RiskGapStatusUpdateAttributes;
  // When true, target the BFF API endpoint; otherwise the legacy standalone Next.js route.
  // Gated by the `ctw-bff-risk-gap-status-updates` flag at the call site.
  useBffEndpoint: boolean;
}

/**
 * Posts a risk-gap status update to either the BFF API or the legacy standalone route. Both
 * accept the same JSON:API request body and expose an equivalent `ok`/`data` fetch result, so
 * callers keep their own success/error handling and only differ in which endpoint is targeted.
 */
export const submitRiskGapStatusUpdate = ({
  ctwFetch,
  env,
  authToken,
  riskGapId,
  attributes,
  useBffEndpoint,
}: SubmitRiskGapStatusUpdateArgs): ReturnType<CtwFetchFn> => {
  const url = useBffEndpoint
    ? `${getBffApiBaseUrl(env)}/v1/risk-gaps/${riskGapId}/status-updates`
    : `${getStandaloneBaseUrl(env)}/api/risk-gaps/${riskGapId}/status-updates`;

  return ctwFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      data: {
        type: 'risk-gap-status-update',
        attributes,
      },
    }),
  });
};
